import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as RT from '@radix-ui/react-tabs';
import { cn } from '../../lib/cn';

/**
 * Tabs primitive (Compose API over Radix UI).
 *
 * Active tab uses `data-[state=active]` to display the brand orange underline
 * and bright text; inactive tabs render in muted color. Tab contents respect
 * the `focus-visible` ring for keyboard users.
 */
export const Tabs = RT.Root;

export const TabsList = forwardRef<
  ElementRef<typeof RT.List>,
  ComponentPropsWithoutRef<typeof RT.List>
>(function TabsList({ className, ...props }, ref) {
  return (
    <RT.List
      ref={ref}
      className={cn('inline-flex items-center gap-1 border-b border-border', className)}
      {...props}
    />
  );
});

export const TabsTrigger = forwardRef<
  ElementRef<typeof RT.Trigger>,
  ComponentPropsWithoutRef<typeof RT.Trigger>
>(function TabsTrigger({ className, ...props }, ref) {
  return (
    <RT.Trigger
      ref={ref}
      className={cn(
        'inline-flex h-10 items-center justify-center whitespace-nowrap border-b-2 border-transparent ' +
          'px-3 text-sm font-medium text-muted transition-colors hover:text-white ' +
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
          'focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
          'data-[state=active]:border-primary data-[state=active]:text-white ' +
          'disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      {...props}
    />
  );
});

export const TabsContent = forwardRef<
  ElementRef<typeof RT.Content>,
  ComponentPropsWithoutRef<typeof RT.Content>
>(function TabsContent({ className, ...props }, ref) {
  return (
    <RT.Content
      ref={ref}
      className={cn(
        'mt-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
          'focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-card',
        className,
      )}
      {...props}
    />
  );
});
